import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    nav: { home: 'Home', services: 'Services', countries: 'Countries', about: 'About', portfolio: 'Campaigns', testimonials: 'Testimonials', contact: 'Contact' },
    header: { cta: 'Start a Project' },
    hero: {
      badge: '#1 African Influencer Marketing Agency',
      titlePre: 'Connect Your Brand with',
      titleHighlight: 'African Influencers',
      description: "Fegga is one of the few agencies specialized in African influencer marketing. We connect businesses with 133 influencers across multiple African countries — speaking English, French, Arabic, Portuguese, Yoruba, Zulu, Lingala and other African languages.",
      ctaPrimary: 'Start a Campaign',
      ctaSecondary: 'See Our Success Stories',
      statInfluencers: 'Influencers',
      statReach: 'Total Reach',
      statCampaigns: 'Campaigns'
    },
    services: {
      label: 'Our Services',
      titlePre: 'Complete Solutions for Your',
      titleHighlight: 'Success',
      subtitle: 'From strategy to execution, we support your brand at every step',
      learnMore: 'Learn more',
      ctaQuestion: 'Have a project in mind?',
      ctaLink: "Let's talk →",
      items: [
        { title: 'Influence Strategy', description: 'We craft tailored strategies that connect your brand with the most relevant African influencers for your audience.', icon: 'target' },
        { title: 'Campaign Management', description: 'From concept to execution, we manage your influencer campaigns end-to-end with real-time performance tracking.', icon: 'trending-up' },
        { title: 'Analytics & Reporting', description: 'Measure the real impact of your campaigns with our advanced analytics tools and detailed reports.', icon: 'bar-chart' },
        { title: 'Content Creation', description: 'Our creative teams produce authentic, engaging content adapted to local African cultures.', icon: 'pen-tool' }
      ]
    },
    countries: {
      label: 'Our Presence',
      titlePre: 'Multiple African Countries,',
      titleHighlight: 'One Unique Network',
      subtitle: "We cover Africa's most dynamic markets with a network of 133 influencers",
      languagesTitle: "Our influencers speak your audience's language",
      languages: ['English', 'French', 'Arabic', 'Portuguese', 'Yoruba', 'Zulu', 'Lingala', 'Other African languages'],
      statInfluencers: 'Active Influencers',
      statReach: 'Total Reach',
      statCampaigns: 'Campaigns Delivered',
      statSatisfaction: 'Satisfaction Rate',
      list: [
        { name: 'Morocco', flag: '🇲🇦', influencers: '14+' },
        { name: 'Tunisia', flag: '🇹🇳', influencers: '8+' },
        { name: 'Nigeria', flag: '🇳🇬', influencers: '20+' },
        { name: 'Ghana', flag: '🇬🇭', influencers: '12+' },
        { name: 'Angola', flag: '🇦🇴', influencers: '6+' },
        { name: 'Ivory Coast', flag: '🇨🇮', influencers: '12+' },
        { name: 'South Africa', flag: '🇿🇦', influencers: '14+' },
        { name: 'Tanzania', flag: '🇹🇿', influencers: '8+' },
        { name: 'DR Congo', flag: '🇨🇩', influencers: '12+' },
        { name: 'Senegal', flag: '🇸🇳', influencers: '8+' },
        { name: 'Ethiopia', flag: '🇪🇹', influencers: '6+' },
        { name: 'Algeria', flag: '🇩🇿', influencers: '5+' },
        { name: 'Botswana', flag: '🇧🇼', influencers: '4+' },
        { name: 'Madagascar', flag: '🇲🇬', influencers: '4+' }
      ]
    },
    about: {
      label: 'About Us',
      titlePre: 'The Story Behind',
      titleHighlight: 'Fegga',
      story1a: 'Born in ',
      story1b: 'Kinshasa',
      story1c: ", Fegga was created with one conviction: African voices deserve to be at the heart of the world's marketing conversations. While most agencies overlooked the continent, we built a network of ",
      story1d: '133 influencers',
      story1e: " across Africa's most dynamic markets.",
      story2: 'Today, we help brands speak to African audiences in their own languages — English, French, Arabic, Portuguese, Yoruba, Zulu, Lingala and many more — through creators their communities already know and trust.',
      missionTitle: 'Our Mission',
      missionText: 'To connect brands with the authentic voices of Africa and turn cultural connection into measurable business results.',
      values: [
        { title: 'Authenticity', description: 'We only build partnerships between brands and influencers whose audiences genuinely match.' },
        { title: 'Transparency', description: 'Clear reporting, real numbers and honest advice at every stage of your campaign.' },
        { title: 'Local Expertise', description: 'Our teams live and work in the markets we serve — we understand every culture we touch.' }
      ]
    },
    portfolio: {
      label: 'Our Campaigns',
      titlePre: 'Campaigns We',
      titleHighlight: 'Run For You',
      subtitle: "From product launches to continent-wide awareness — here's what we can deliver for your brand",
      featuredLabel: 'Client Campaign',
      featured: {
        client: 'Sanchez Herrera',
        role: 'Hotel Branch Manager',
        country: 'Egypt',
        flag: '🇪🇬',
        campaign: 'Hospitality Influencer Campaign',
        description: 'We partnered with Sanchez Herrera, manager of a hotel branch in Egypt, to promote the establishment through authentic influencer marketing.',
        stats: [
          { value: '2', label: 'Local Influencers' },
          { value: '500K+', label: 'Combined Followers' },
          { value: '2M', label: 'People Reached' }
        ]
      },
      img1Title: 'Tailored Strategy',
      img1Text: 'Every campaign is unique and adapted to your goals',
      img2Title: 'Data & Analytics',
      img2Text: 'Track performance in real time with our advanced tools',
      campaigns: [
        { title: 'Product Launches', description: 'Introduce your product to African audiences through creators they trust.', points: ['Multi-influencer launch rollout', 'Reels, stories & unboxing content', 'Launch-day buzz across countries'] },
        { title: 'Brand Awareness', description: 'Grow your visibility across multiple African markets at once.', points: ['Localized content in each language', 'Cross-country influencer squads', 'Measurable reach & engagement reports'] },
        { title: 'Events & Activations', description: 'Give your events massive live coverage by local voices.', points: ['Live stories & on-site coverage', 'Local influencer attendance', 'Post-event highlight content'] }
      ]
    },
    testimonials: {
      label: 'Testimonials',
      titlePre: 'What Our Clients',
      titleHighlight: 'Say About Us',
      subtitle: "Our clients' satisfaction is our greatest achievement",
      satisfaction: 'Client Satisfaction',
      avgRating: 'Average Rating',
      happyClients: 'Happy Clients',
      items: [
        { name: 'Amina Diallo', position: 'Marketing Director', company: 'AfriTech Solutions', country: 'Senegal', avatar: 'AD', content: 'Fegga transformed our marketing approach in Africa. Their expertise in local markets and their influencer network are unmatched.', rating: 5 },
        { name: 'Mohammed El Fassi', position: 'CEO', company: 'Moroccan Startup Hub', country: 'Morocco', avatar: 'ME', content: 'Exceptional results! Our campaign exceeded all our expectations thanks to their precise selection of Moroccan influencers.', rating: 5 },
        { name: 'Sarah Okonkwo', position: 'Brand Manager', company: 'Lagos Fashion Group', country: 'Nigeria', avatar: 'SO', content: 'The Fegga team truly understands the cultural nuances of each African market. A trusted partner.', rating: 5 }
      ]
    },
    contact: {
      label: 'Contact',
      titlePre: 'Ready to Launch Your',
      titleHighlight: 'Campaign?',
      subtitle: "Contact us today and let's start building your success together",
      nameLabel: 'Full Name *',
      namePlaceholder: 'Your name',
      emailLabel: 'Email *',
      emailPlaceholder: 'your@email.com',
      companyLabel: 'Company',
      companyPlaceholder: 'Your company',
      countryLabel: 'Country',
      countryPlaceholder: 'Your country',
      messageLabel: 'Message *',
      messagePlaceholder: 'Describe your project...',
      submit: 'Send Message',
      sending: 'Sending...',
      successTitle: 'Message Sent!',
      successText: 'We will get back to you as soon as possible.',
      toastSuccess: 'Your message has been sent successfully!',
      toastError: 'Something went wrong. Please try again or reach us on WhatsApp.',
      emailTitle: 'Email',
      phoneTitle: 'Phone',
      hqTitle: 'Headquarters',
      hqValue: 'Kinshasa, Democratic Republic of Congo',
      ctaTitle: 'Need a quick answer?',
      ctaText: 'Our team is available Monday to Friday, 9am to 6pm (GMT+1). You can also reach us on WhatsApp at +243 895 534 241.',
      online: 'Online now'
    },
    footer: {
      description: "Africa's #1 influencer marketing agency. We connect brands with the most relevant influencers across multiple African countries, speaking English, French, Arabic, Portuguese, Yoruba, Zulu, Lingala and other African languages.",
      servicesTitle: 'Services',
      companyTitle: 'Company',
      marketsTitle: 'Our Markets',
      companyLinks: ['About', 'Campaigns', 'Testimonials', 'Contact'],
      serviceLinks: ['Influence Strategy', 'Campaign Management', 'Analytics & Reporting', 'Content Creation'],
      markets: ['Morocco', 'Algeria', 'Tunisia', 'Nigeria', 'Ghana', 'Senegal', 'Ivory Coast', 'DR Congo', 'Ethiopia', 'Tanzania', 'Angola', 'South Africa', 'Botswana', 'Madagascar'],
      address: 'Kinshasa, Democratic Republic of Congo',
      rights: 'All rights reserved.',
      madeWith: 'Made with ❤️ for Africa',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service'
    },
    whatsapp: {
      tooltip: 'Chat with us on WhatsApp',
      message: "Hello Fegga! I'd like to discuss an influencer marketing campaign."
    },
    instagram: {
      title: 'See Our Work on Instagram',
      text: 'Our Instagram is where we showcase our influencers, campaigns and behind-the-scenes.',
      cta: 'Follow @fegga.agency'
    }
  },
  fr: {
    nav: { home: 'Accueil', services: 'Services', countries: 'Pays', about: 'À Propos', portfolio: 'Campagnes', testimonials: 'Témoignages', contact: 'Contact' },
    header: { cta: 'Démarrer un Projet' },
    hero: {
      badge: "Agence #1 en Marketing d'Influence Africain",
      titlePre: 'Connectez Votre Marque aux',
      titleHighlight: 'Influenceurs Africains',
      description: "Fegga est l'une des rares agences spécialisées dans le marketing d'influence africain. Nous connectons les entreprises avec 133 influenceurs à travers plusieurs pays d'Afrique — parlant anglais, français, arabe, portugais, yoruba, zoulou, lingala et d'autres langues africaines.",
      ctaPrimary: 'Démarrer une Campagne',
      ctaSecondary: 'Voir nos Réussites',
      statInfluencers: 'Influenceurs',
      statReach: 'Portée Totale',
      statCampaigns: 'Campagnes'
    },
    services: {
      label: 'Nos Services',
      titlePre: 'Des Solutions Complètes pour Votre',
      titleHighlight: 'Succès',
      subtitle: "De la stratégie à l'exécution, nous accompagnons votre marque à chaque étape",
      learnMore: 'En savoir plus',
      ctaQuestion: 'Vous avez un projet en tête ?',
      ctaLink: 'Parlons-en →',
      items: [
        { title: "Stratégie d'Influence", description: 'Nous créons des stratégies personnalisées qui connectent votre marque avec les influenceurs africains les plus pertinents pour votre audience.', icon: 'target' },
        { title: 'Gestion de Campagnes', description: "De la conception à l'exécution, nous gérons vos campagnes d'influence de A à Z avec un suivi en temps réel des performances.", icon: 'trending-up' },
        { title: 'Analytics & Reporting', description: "Mesurez l'impact réel de vos campagnes grâce à nos outils d'analyse avancés et rapports détaillés.", icon: 'bar-chart' },
        { title: 'Création de Contenu', description: 'Nos équipes créatives produisent du contenu authentique et engageant adapté aux cultures locales africaines.', icon: 'pen-tool' }
      ]
    },
    countries: {
      label: 'Notre Présence',
      titlePre: "Plusieurs Pays d'Afrique,",
      titleHighlight: 'Un Réseau Unique',
      subtitle: "Nous couvrons les marchés les plus dynamiques d'Afrique avec un réseau de 133 influenceurs",
      languagesTitle: 'Nos influenceurs parlent la langue de votre audience',
      languages: ['Anglais', 'Français', 'Arabe', 'Portugais', 'Yoruba', 'Zoulou', 'Lingala', 'Autres langues africaines'],
      statInfluencers: 'Influenceurs Actifs',
      statReach: 'Portée Totale',
      statCampaigns: 'Campagnes Menées',
      statSatisfaction: 'Taux de Satisfaction',
      list: [
        { name: 'Maroc', flag: '🇲🇦', influencers: '14+' },
        { name: 'Tunisie', flag: '🇹🇳', influencers: '8+' },
        { name: 'Nigeria', flag: '🇳🇬', influencers: '20+' },
        { name: 'Ghana', flag: '🇬🇭', influencers: '12+' },
        { name: 'Angola', flag: '🇦🇴', influencers: '6+' },
        { name: "Côte d'Ivoire", flag: '🇨🇮', influencers: '12+' },
        { name: 'Afrique du Sud', flag: '🇿🇦', influencers: '14+' },
        { name: 'Tanzanie', flag: '🇹🇿', influencers: '8+' },
        { name: 'RD Congo', flag: '🇨🇩', influencers: '12+' },
        { name: 'Sénégal', flag: '🇸🇳', influencers: '8+' },
        { name: 'Éthiopie', flag: '🇪🇹', influencers: '6+' },
        { name: 'Algérie', flag: '🇩🇿', influencers: '5+' },
        { name: 'Botswana', flag: '🇧🇼', influencers: '4+' },
        { name: 'Madagascar', flag: '🇲🇬', influencers: '4+' }
      ]
    },
    about: {
      label: 'À Propos',
      titlePre: "L'Histoire de",
      titleHighlight: 'Fegga',
      story1a: 'Née à ',
      story1b: 'Kinshasa',
      story1c: ", Fegga a été créée avec une conviction : les voix africaines méritent d'être au cœur des conversations marketing mondiales. Alors que la plupart des agences négligeaient le continent, nous avons bâti un réseau de ",
      story1d: '133 influenceurs',
      story1e: " à travers les marchés les plus dynamiques d'Afrique.",
      story2: "Aujourd'hui, nous aidons les marques à parler aux audiences africaines dans leurs propres langues — anglais, français, arabe, portugais, yoruba, zoulou, lingala et bien d'autres — grâce à des créateurs que leurs communautés connaissent et en qui elles ont confiance.",
      missionTitle: 'Notre Mission',
      missionText: "Connecter les marques aux voix authentiques de l'Afrique et transformer la connexion culturelle en résultats commerciaux mesurables.",
      values: [
        { title: 'Authenticité', description: 'Nous ne créons que des partenariats entre marques et influenceurs dont les audiences correspondent réellement.' },
        { title: 'Transparence', description: 'Des rapports clairs, des chiffres réels et des conseils honnêtes à chaque étape de votre campagne.' },
        { title: 'Expertise Locale', description: 'Nos équipes vivent et travaillent dans les marchés que nous servons — nous comprenons chaque culture.' }
      ]
    },
    portfolio: {
      label: 'Nos Campagnes',
      titlePre: 'Des Campagnes',
      titleHighlight: 'À Votre Mesure',
      subtitle: "Du lancement de produit à la notoriété à l'échelle du continent — voici ce que nous pouvons réaliser pour votre marque",
      featuredLabel: 'Campagne Client',
      featured: {
        client: 'Sanchez Herrera',
        role: "Manager d'une branche hôtelière",
        country: 'Égypte',
        flag: '🇪🇬',
        campaign: "Campagne d'Influence Hôtelière",
        description: "Nous avons collaboré avec Sanchez Herrera, manager d'une branche hôtelière en Égypte, pour promouvoir l'établissement grâce à un marketing d'influence authentique.",
        stats: [
          { value: '2', label: 'Influenceurs Locaux' },
          { value: '500K+', label: 'Abonnés Cumulés' },
          { value: '2M', label: 'Personnes Touchées' }
        ]
      },
      img1Title: 'Stratégie Personnalisée',
      img1Text: 'Chaque campagne est unique et adaptée à vos objectifs',
      img2Title: 'Données & Analytics',
      img2Text: 'Suivez les performances en temps réel avec nos outils avancés',
      campaigns: [
        { title: 'Lancements de Produits', description: 'Présentez votre produit aux audiences africaines via des créateurs de confiance.', points: ['Déploiement multi-influenceurs', 'Reels, stories & contenus unboxing', 'Buzz le jour du lancement dans plusieurs pays'] },
        { title: 'Notoriété de Marque', description: 'Développez votre visibilité sur plusieurs marchés africains à la fois.', points: ['Contenu localisé dans chaque langue', "Équipes d'influenceurs multi-pays", "Rapports de portée et d'engagement mesurables"] },
        { title: 'Événements & Activations', description: 'Offrez à vos événements une couverture live massive par des voix locales.', points: ['Stories en direct & couverture sur place', "Présence d'influenceurs locaux", 'Contenus highlights post-événement'] }
      ]
    },
    testimonials: {
      label: 'Témoignages',
      titlePre: 'Ce Que Nos Clients',
      titleHighlight: 'Disent de Nous',
      subtitle: 'La satisfaction de nos clients est notre plus grande réussite',
      satisfaction: 'Satisfaction Client',
      avgRating: 'Note Moyenne',
      happyClients: 'Clients Satisfaits',
      items: [
        { name: 'Amina Diallo', position: 'Directrice Marketing', company: 'AfriTech Solutions', country: 'Sénégal', avatar: 'AD', content: "Fegga a transformé notre approche marketing en Afrique. Leur expertise des marchés locaux et leur réseau d'influenceurs sont incomparables.", rating: 5 },
        { name: 'Mohammed El Fassi', position: 'CEO', company: 'Moroccan Startup Hub', country: 'Maroc', avatar: 'ME', content: "Résultats exceptionnels ! Notre campagne a dépassé toutes nos attentes grâce à leur sélection précise d'influenceurs marocains.", rating: 5 },
        { name: 'Sarah Okonkwo', position: 'Brand Manager', company: 'Lagos Fashion Group', country: 'Nigeria', avatar: 'SO', content: "L'équipe Fegga comprend vraiment les nuances culturelles de chaque marché africain. Un partenaire de confiance.", rating: 5 }
      ]
    },
    contact: {
      label: 'Contact',
      titlePre: 'Prêt à Lancer Votre',
      titleHighlight: 'Campagne ?',
      subtitle: "Contactez-nous dès aujourd'hui et commençons à construire votre succès ensemble",
      nameLabel: 'Nom complet *',
      namePlaceholder: 'Votre nom',
      emailLabel: 'Email *',
      emailPlaceholder: 'votre@email.com',
      companyLabel: 'Entreprise',
      companyPlaceholder: 'Votre entreprise',
      countryLabel: 'Pays',
      countryPlaceholder: 'Votre pays',
      messageLabel: 'Message *',
      messagePlaceholder: 'Décrivez votre projet...',
      submit: 'Envoyer le Message',
      sending: 'Envoi en cours...',
      successTitle: 'Message Envoyé !',
      successText: 'Nous vous répondrons dans les plus brefs délais.',
      toastSuccess: 'Votre message a été envoyé avec succès !',
      toastError: 'Une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.',
      emailTitle: 'Email',
      phoneTitle: 'Téléphone',
      hqTitle: 'Siège',
      hqValue: 'Kinshasa, République Démocratique du Congo',
      ctaTitle: "Besoin d'une réponse rapide ?",
      ctaText: 'Notre équipe est disponible du lundi au vendredi de 9h à 18h (GMT+1). Vous pouvez aussi nous joindre sur WhatsApp au +243 895 534 241.',
      online: 'En ligne maintenant'
    },
    footer: {
      description: "L'agence de marketing d'influence #1 en Afrique. Nous connectons les marques aux influenceurs les plus pertinents à travers plusieurs pays d'Afrique, parlant anglais, français, arabe, portugais, yoruba, zoulou, lingala et d'autres langues africaines.",
      servicesTitle: 'Services',
      companyTitle: 'Entreprise',
      marketsTitle: 'Nos Marchés',
      companyLinks: ['À Propos', 'Campagnes', 'Témoignages', 'Contact'],
      serviceLinks: ["Stratégie d'Influence", 'Gestion de Campagnes', 'Analytics & Reporting', 'Création de Contenu'],
      markets: ['Maroc', 'Algérie', 'Tunisie', 'Nigeria', 'Ghana', 'Sénégal', "Côte d'Ivoire", 'RD Congo', 'Éthiopie', 'Tanzanie', 'Angola', 'Afrique du Sud', 'Botswana', 'Madagascar'],
      address: 'Kinshasa, République Démocratique du Congo',
      rights: 'Tous droits réservés.',
      madeWith: "Conçu avec ❤️ pour l'Afrique",
      privacy: 'Politique de confidentialité',
      terms: "Conditions d'utilisation"
    },
    whatsapp: {
      tooltip: 'Discutez avec nous sur WhatsApp',
      message: "Bonjour Fegga ! J'aimerais discuter d'une campagne de marketing d'influence."
    },
    instagram: {
      title: 'Découvrez Notre Travail sur Instagram',
      text: 'Notre Instagram présente nos influenceurs, nos campagnes et nos coulisses.',
      cta: 'Suivre @fegga.agency'
    }
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('fegga-lang') || 'en');

  useEffect(() => {
    localStorage.setItem('fegga-lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
